import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { createIconComponent } from 'uikit/icons';

import FormFieldLabel from './formFieldLabel';
import FormFieldHelp from './formFieldHelp';
import FormFieldError from './formFieldError';
import { shouldTypeUseFieldset, shouldTypeUseHeadingAsLabel } from '../helpers';

import styles from '../styles/formField.module.scss';

const FormField = ({
  children = null,
  className = '',
  error = '',
  featureCategory = 'default',
  featureIcon = null,
  fieldId = '',
  hasError = false,
  help = '',
  isDirty = false,
  isFeature = false,
  isRequired = false,
  label = '',
  schema,
  shouldUseFieldset = false,
  shouldUseHeadingAsLabel = false,
  testId = '',
  type,
}) => {
  const isHeadingLabel = shouldUseHeadingAsLabel || shouldTypeUseHeadingAsLabel(type);
  const isFieldset = (shouldUseFieldset || shouldTypeUseFieldset(type)) && !isHeadingLabel;

  const Wrapper = isFieldset ? 'fieldset' : 'div';
  const Header = isFieldset ? 'legend' : 'div';

  const renderIcon = () => {
    if (!featureIcon) return;

    const isCustomIcon = typeof featureIcon === 'string';
    const iconTestId = fieldId ? `${fieldId}-icon` : 'icon';

    const iconProps = {
      className: classnames(styles.icon),
      ...(isCustomIcon ? { testId: iconTestId } : { 'data-testid': iconTestId }),
    };

    return createIconComponent({ icon: featureIcon, ...iconProps });
  };

  return (
    <Wrapper
      className={classnames(styles.element, { [styles.isFeature]: isFeature }, className)}
      data-testid={testId}
    >
      {isFeature && (
        <div
          className={classnames(styles.iconContainer, {
            [styles.isFeatureCategoryAi]: featureCategory === 'ai',
          })}
          data-testid="icon-container"
        >
          {renderIcon()}
        </div>
      )}
      <div className={styles.content}>
        <Header className={styles.header}>
          <FormFieldLabel
            isRequired={isRequired}
            label={label}
            fieldId={fieldId}
            LabelAddOn={schema.LabelAddOn}
            isInputLabel={isHeadingLabel}
          />
        </Header>
        <FormFieldHelp className={styles.help} help={help} />
        {children}
        <FormFieldError
          hasError={hasError}
          isDirty={isDirty}
          isHidden={schema.shouldHideErrors}
          error={error}
        />
      </div>
    </Wrapper>
  );
};

FormField.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  error: PropTypes.string,
  featureCategory: PropTypes.oneOf(['default', 'ai']),
  featureIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  fieldId: PropTypes.string,
  hasError: PropTypes.bool,
  help: PropTypes.string,
  isDirty: PropTypes.bool,
  isFeature: PropTypes.bool,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  schema: PropTypes.shape({
    isDisabled: PropTypes.bool,
    LabelAddOn: PropTypes.elementType,
    placeholder: PropTypes.string,
    shouldHideErrors: PropTypes.bool,
  }).isRequired,
  shouldUseFieldset: PropTypes.bool,
  shouldUseHeadingAsLabel: PropTypes.bool,
  testId: PropTypes.string,
  type: PropTypes.string.isRequired,
};

export { FormField as FormFieldStorybook };
export default React.memo(FormField);
